import { HELD_SEAT_METHOD, isGameId, isHiddenGame, type GameId } from "@bing/shared";
import { Client } from "colyseus.js";

/**
 * WHERE IS MY HELD SEAT? — ASKED OF THE GAME-SERVER, NOT OF THIS DEVICE (card-9f, the operator
 * 2026-10-28: «the refusal's two options really work»).
 *
 * The «بازی عمومی» refusal (`PUBLIC_GAME_IN_PROGRESS_MESSAGE`) used to offer «بازگشت به بازی» only when
 * THIS device kept the seat's ticket — and 404 of 606 held seats in a week were never returned to: the
 * phone dropped before the dealt board was on its screen, or it was another device. The game-server
 * knows: it wrote the hold. So the refusal screen asks it, by this launch's initData (in the BODY —
 * nothing rides the URL), which public table holds this player's seat (`HELD_SEAT_METHOD`); the seat
 * itself is then handed back by the `takeover` door (`seat-takeover.ts`, origin `held`).
 *
 * THREE ANSWERS AND NO FOURTH, and it NEVER throws:
 *  · `held` — the table (in progress, or a public waiting room);
 *  · `none` — the game-server says nothing is held (or names a game this app does not offer);
 *  · `unknown` — no answer worth acting on: an older game-server («invalid method», 4210), a drain's
 *    503, the network, the bound, a body that is not the contract, no initData.
 * `none` and `unknown` both leave the refusal screen exactly as it was before this change.
 */
export type HeldSeatLookup =
  | { kind: "held"; roomId: string; game: GameId; waiting: boolean }
  | { kind: "none" }
  | { kind: "unknown" };

/** How long the refusal screen waits for the answer before it stays as it is. */
export const HELD_SEAT_LOOKUP_TIMEOUT_MS = 1_500;

const UNKNOWN: HeldSeatLookup = { kind: "unknown" };
const NONE: HeldSeatLookup = { kind: "none" };

/** The colyseus.js 0.15 private `http` the matchmake POSTs ride (as `takeSeatBack` uses it). */
interface MatchmakeHttp {
  http?: { post?: (path: string, options: unknown) => Promise<{ data?: unknown }> };
}

/** Read the game-server's answer — the contract, or `unknown`. */
function readAnswer(data: unknown): HeldSeatLookup {
  if (typeof data !== "object" || data === null || !("held" in data)) return UNKNOWN;
  const held = (data as { held: unknown }).held;
  if (held === null) return NONE;
  if (typeof held !== "object") return UNKNOWN;
  const { roomId, game, waiting } = held as { roomId?: unknown; game?: unknown; waiting?: unknown };
  if (typeof roomId !== "string" || roomId.length === 0) return UNKNOWN;
  if (typeof game !== "string" || typeof waiting !== "boolean") return UNKNOWN;
  // A game this app does not know, or one hidden from players, is never a door (the strip's rule).
  if (!isGameId(game) || isHiddenGame(game)) return NONE;
  return { kind: "held", roomId, game, waiting };
}

export async function askHeldSeat(
  wsUrl: string,
  initData: string,
  opts: { timeoutMs?: number; signal?: AbortSignal; client?: unknown } = {},
): Promise<HeldSeatLookup> {
  if (initData.length === 0 || opts.signal?.aborted) return UNKNOWN;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let onAbort: (() => void) | undefined;
  try {
    const client = (opts.client ?? new Client(wsUrl)) as MatchmakeHttp;
    const post = client.http?.post;
    if (typeof post !== "function") return UNKNOWN;
    const asked = post
      .call(client.http, `matchmake/${HELD_SEAT_METHOD}/self`, {
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({ initData }),
      })
      .then((response) => readAnswer(response?.data))
      .catch((): HeldSeatLookup => UNKNOWN);
    const bounded = new Promise<HeldSeatLookup>((resolve) => {
      timer = setTimeout(() => resolve(UNKNOWN), opts.timeoutMs ?? HELD_SEAT_LOOKUP_TIMEOUT_MS);
      onAbort = () => resolve(UNKNOWN);
      opts.signal?.addEventListener("abort", onAbort, { once: true });
    });
    return await Promise.race([asked, bounded]);
  } catch {
    return UNKNOWN;
  } finally {
    if (timer !== undefined) clearTimeout(timer);
    if (onAbort) opts.signal?.removeEventListener("abort", onAbort);
  }
}
